<template>
  <div class="modal fade" id="createPayrollPeriodModal" tabindex="-1" aria-labelledby="createPayrollPeriodModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="createPayrollPeriodModalLabel">Create New Payroll Period</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submitPayrollPeriod">
            <div class="row g-3">
              <div class="col-md-12">
                <label class="form-label">Period Name <span class="text-danger">*</span></label>
                <div class="row g-2">
                  <div class="col-md-5">
                    <select
                        v-model="periodForm.month"
                        class="form-select"
                        required
                        @change="updatePeriodName"
                    >
                      <option value="">Select Month</option>
                      <option v-for="(month, index) in months" :key="index" :value="index">
                        {{ month }}
                      </option>
                    </select>
                  </div>
                  <div class="col-md-3">
                    <select
                        v-model="periodForm.year"
                        class="form-select"
                        required
                        @change="updatePeriodName"
                    >
                      <option value="">Select Year</option>
                      <option v-for="year in yearOptions" :key="year" :value="year">
                        {{ year }}
                      </option>
                    </select>
                  </div>
                  <div class="col-md-4">
                    <select
                        v-model="periodForm.type"
                        class="form-select"
                        required
                        @change="updatePeriodName"
                    >
                      <option value="Payroll">Regular Payroll</option>
                      <option value="Bonus">Bonus Payroll</option>
                      <option value="Commission">Commission Payroll</option>
                      <option value="Special">Special Payroll</option>
                    </select>
                  </div>
                </div>
                <small class="text-muted mt-1 d-block">
                  <i class="mdi mdi-information-outline me-1"></i>
                  Period: <strong>{{ generatedPeriodName || 'Please select month and year' }}</strong>
                </small>
              </div>

              <div class="col-md-6">
                <label class="form-label">Start Date <span class="text-danger">*</span></label>
                <input
                    v-model="periodForm.start_date"
                    type="date"
                    class="form-control"
                    required
                    @change="validateDateRange"
                >
              </div>

              <div class="col-md-6">
                <label class="form-label">End Date <span class="text-danger">*</span></label>
                <input
                    v-model="periodForm.end_date"
                    type="date"
                    class="form-control"
                    required
                    @change="validateDateRange"
                >
                <small v-if="dateRangeError" class="text-danger">{{ dateRangeError }}</small>
              </div>

              <div class="col-md-6">
                <label class="form-label">Payment Date <span class="text-danger">*</span></label>
                <input
                    v-model="periodForm.payment_date"
                    type="date"
                    class="form-control"
                    required
                    @change="validatePaymentDate"
                >
                <small v-if="paymentDateError" class="text-danger">{{ paymentDateError }}</small>
              </div>

              <div class="col-md-6">
                <label class="form-label">Employee Selection</label>
                <select v-model="periodForm.employee_selection" class="form-select">
                  <option value="all">All Active Employees</option>
                  <option value="department">By Department</option>
                  <option value="custom">Custom Selection</option>
                </select>
              </div>

              <div v-if="periodForm.employee_selection === 'department'" class="col-md-12">
                <label class="form-label">Select Departments</label>
                <div class="card">
                  <div class="card-body" style="max-height: 200px; overflow-y: auto;">
                    <div class="row">
                      <div class="col-md-6" v-for="dept in departments" :key="dept.id">
                        <div class="form-check mb-2">
                          <input
                              class="form-check-input"
                              type="checkbox"
                              :id="`dept-${dept.id}`"
                              :value="dept.id"
                              v-model="selectedDepartments"
                          >
                          <label class="form-check-label" :for="`dept-${dept.id}`">
                            {{ dept.departmentName }}
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <small class="text-muted">{{ selectedDepartments.length }} department(s) selected</small>
              </div>

              <div class="col-md-12">
                <label class="form-label">Notes</label>
                <textarea
                    v-model="periodForm.notes"
                    class="form-control"
                    rows="3"
                    placeholder="Additional notes about this payroll period"
                ></textarea>
              </div>

              <div class="col-md-12">
                <div class="form-check">
                  <input
                      class="form-check-input"
                      type="checkbox"
                      id="includeAttendance"
                      v-model="periodForm.include_attendance"
                  >
                  <label class="form-check-label" for="includeAttendance">
                    Include attendance data in payroll calculation
                  </label>
                </div>
              </div>

              <div class="col-md-12">
                <div class="form-check">
                  <input
                      class="form-check-input"
                      type="checkbox"
                      id="includeAdjustments"
                      v-model="periodForm.include_adjustments"
                  >
                  <label class="form-check-label" for="includeAdjustments">
                    Include pending salary adjustments (bonuses, commissions, etc.)
                  </label>
                </div>
              </div>

              <div class="col-md-12">
                <div class="form-check">
                  <input
                      class="form-check-input"
                      type="checkbox"
                      id="autoProcess"
                      v-model="periodForm.auto_process"
                  >
                  <label class="form-check-label" for="autoProcess">
                    Automatically process payroll calculations after creation
                  </label>
                </div>
              </div>

              <div class="col-md-12 alert alert-info" v-if="showPeriodOverlapWarning">
                <i class="mdi mdi-information-outline me-2"></i>
                Warning: This period overlaps with existing payroll periods. Please verify the date range.
              </div>

              <!-- Quick Actions for Date Setting -->
              <div class="col-md-12">
                <label class="form-label">Quick Date Setup</label>
                <div class="btn-group" role="group">
                  <button type="button" class="btn btn-outline-primary btn-sm" @click="setCurrentMonth">
                    Current Month
                  </button>
                  <button type="button" class="btn btn-outline-primary btn-sm" @click="setLastMonth">
                    Last Month
                  </button>
                  <button type="button" class="btn btn-outline-primary btn-sm" @click="setNextMonth">
                    Next Month
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
          <button
              type="button"
              class="btn btn-primary"
              @click="submitPayrollPeriod"
              :disabled="submitLoading || !isFormValid"
          >
            <span v-if="submitLoading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Create Payroll Period
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, execute } from "@/api";

export default {
  name: 'CreatePayrollPeriod',
  props: {
    departments: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      periodForm: {
        month: '',
        year: '',
        type: 'Payroll',
        period_name: '',
        start_date: '',
        end_date: '',
        payment_date: '',
        employee_selection: 'all',
        notes: '',
        include_attendance: true,
        include_adjustments: true,
        auto_process: false
      },
      selectedDepartments: [],
      dateRangeError: '',
      paymentDateError: '',
      showPeriodOverlapWarning: false,
      submitLoading: false,
      modal: null,
      months: [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ]
    };
  },
  computed: {
    isFormValid() {
      return this.periodForm.month !== '' &&
          this.periodForm.year !== '' &&
          this.periodForm.start_date &&
          this.periodForm.end_date &&
          this.periodForm.payment_date &&
          !this.dateRangeError &&
          !this.paymentDateError &&
          (this.periodForm.employee_selection !== 'department' || this.selectedDepartments.length > 0);
    },
    yearOptions() {
      const currentYear = new Date().getFullYear();
      const years = [];
      // Show 2 years back and 1 year forward
      for (let i = currentYear - 2; i <= currentYear + 1; i++) {
        years.push(i);
      }
      return years;
    },
    generatedPeriodName() {
      if (this.periodForm.month !== '' && this.periodForm.year) {
        return `${this.months[this.periodForm.month]} ${this.periodForm.year} ${this.periodForm.type}`;
      }
      return '';
    }
  },
  mounted() {
    // Initialize Bootstrap modal
    this.initModal();
    // Set default to current month/year
    this.setDefaultMonthYear();
  },
  methods: {
    initModal() {
      this.$nextTick(() => {
        // Using Bootstrap's native JS to initialize modal
        this.modal = new bootstrap.Modal(document.getElementById('createPayrollPeriodModal'));

        // Listen for modal hidden event to reset form
        document.getElementById('createPayrollPeriodModal').addEventListener('hidden.bs.modal', this.resetForm);
      });
    },
    showModal() {
      if (this.modal) {
        this.modal.show();
      } else {
        this.initModal();
        setTimeout(() => {
          if (this.modal) this.modal.show();
        }, 100);
      }
    },
    hideModal() {
      if (this.modal) {
        this.modal.hide();
      }
    },
    resetForm() {
      this.periodForm = {
        month: '',
        year: '',
        type: 'Payroll',
        period_name: '',
        start_date: '',
        end_date: '',
        payment_date: '',
        employee_selection: 'all',
        notes: '',
        include_attendance: true,
        include_adjustments: true,
        auto_process: false
      };
      this.selectedDepartments = [];
      this.dateRangeError = '';
      this.paymentDateError = '';
      this.showPeriodOverlapWarning = false;
      this.submitLoading = false;

      // Reset to current month/year on form reset
      this.setDefaultMonthYear();
    },
    setDefaultMonthYear() {
      const now = new Date();
      this.periodForm.month = now.getMonth();
      this.periodForm.year = now.getFullYear();
      this.updatePeriodName();
    },
    updatePeriodName() {
      if (this.periodForm.month !== '' && this.periodForm.year) {
        this.periodForm.period_name = this.generatedPeriodName;

        // Auto-set dates if month and year are selected
        this.autoSetDates();
      }
    },
    autoSetDates() {
      if (this.periodForm.month === '' || !this.periodForm.year) return;

      const year = this.periodForm.year;
      const month = this.periodForm.month;

      // Set start date to first day of selected month
      const startDate = new Date(year, month, 1);

      // Set end date to last day of selected month
      const endDate = new Date(year, month + 1, 0);

      // Set payment date to 5 days after end date (common practice)
      const paymentDate = new Date(endDate);
      paymentDate.setDate(paymentDate.getDate() + 5);

      // Format dates for input fields
      this.periodForm.start_date = this.formatDateForInput(startDate);
      this.periodForm.end_date = this.formatDateForInput(endDate);
      this.periodForm.payment_date = this.formatDateForInput(paymentDate);

      // Validate the dates
      this.validateDateRange();
      this.validatePaymentDate();
    },
    formatDateForInput(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    setCurrentMonth() {
      const now = new Date();
      this.periodForm.month = now.getMonth();
      this.periodForm.year = now.getFullYear();
      this.updatePeriodName();
    },
    setLastMonth() {
      const now = new Date();
      now.setMonth(now.getMonth() - 1);
      this.periodForm.month = now.getMonth();
      this.periodForm.year = now.getFullYear();
      this.updatePeriodName();
    },
    setNextMonth() {
      const now = new Date();
      now.setMonth(now.getMonth() + 1);
      this.periodForm.month = now.getMonth();
      this.periodForm.year = now.getFullYear();
      this.updatePeriodName();
    },
    validateDateRange() {
      if (!this.periodForm.start_date || !this.periodForm.end_date) return;

      const startDate = new Date(this.periodForm.start_date);
      const endDate = new Date(this.periodForm.end_date);

      if (endDate < startDate) {
        this.dateRangeError = 'End date cannot be before start date';
      } else {
        this.dateRangeError = '';
        this.checkPeriodOverlap();
      }
    },
    validatePaymentDate() {
      if (!this.periodForm.end_date || !this.periodForm.payment_date) return;

      const endDate = new Date(this.periodForm.end_date);
      const paymentDate = new Date(this.periodForm.payment_date);

      if (paymentDate < endDate) {
        this.paymentDateError = 'Payment date should be after the end date';
      } else {
        this.paymentDateError = '';
      }
    },
    checkPeriodOverlap() {
      if (!this.periodForm.start_date || !this.periodForm.end_date) return;

      const data = new FormData();
      data.append("function", "checkPeriodOverlap");
      data.append("startDate", this.periodForm.start_date);
      data.append("endDate", this.periodForm.end_date);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.showPeriodOverlapWarning = res.data.data.hasOverlap;
            } else {
              console.error("Error checking period overlap:", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API Error:", error);
          });
    },
    submitPayrollPeriod() {
      if (!this.isFormValid) return;

      this.submitLoading = true;

      const data = new FormData();
      data.append("function", "createPayrollPeriod");
      data.append("period_name", this.periodForm.period_name);
      data.append("start_date", this.periodForm.start_date);
      data.append("end_date", this.periodForm.end_date);
      data.append("payment_date", this.periodForm.payment_date);
      data.append("employee_selection", this.periodForm.employee_selection);
      data.append("include_attendance", this.periodForm.include_attendance ? 1 : 0);
      data.append("include_adjustments", this.periodForm.include_adjustments ? 1 : 0);
      data.append("auto_process", this.periodForm.auto_process ? 1 : 0);

      if (this.periodForm.notes) {
        data.append("notes", this.periodForm.notes);
      }

      if (this.periodForm.employee_selection === 'department' && this.selectedDepartments.length > 0) {
        data.append("departments", JSON.stringify(this.selectedDepartments));
      }

      execute(data, authUrl)
          .then((res) => {
            this.submitLoading = false;

            if (res.data.success) {
              // Emit event to parent with created period data
              this.$emit('period-created', {
                success: res.data.success,
                periodId: res.data.data.periodId,
                periodName: res.data.data.periodName
              });

              // Hide modal
              this.hideModal();

              // Show success alert
              alert(res.data.message || "Payroll period created successfully");
            } else {
              alert(res.data.message || "Failed to create payroll period");
            }
          })
          .catch((error) => {
            this.submitLoading = false;
            console.error("API Error:", error);

            // Display more specific error information to user
            if (error.response && error.response.data && error.response.data.message) {
              alert(`Error: ${error.response.data.message}`);
            } else if (error.message) {
              alert(`Error: ${error.message}`);
            } else {
              alert("An error occurred while creating the payroll period. Please check the console for details.");
            }
          });
    }
  }
};
</script>

<style scoped>
.form-check {
  margin-bottom: 0.5rem;
}

.alert {
  margin-top: 1rem;
}

.btn-group {
  display: flex;
  gap: 0.25rem;
}

.card-body {
  background-color: #f8f9fa;
}

strong {
  color: #0d6efd;
}
</style>